"use client"
import styled from "@emotion/styled"

import { IconButton } from "@mui/material"
import { useRef, useState } from "react"
import { extractImage, extractProperty, getUmbracoMediaUrl } from "utils"
import { Desktop, Mobile } from "../common/bit/MediaQueries"
import Container from "../Container"
import VideoMuteIcon from "../icons/video/VideoMuteIcon"
import VideoPauseIcon from "../icons/video/VideoPauseIcon"
import VideoPlayIcon from "../icons/video/VideoPlayIcon"
import VideoUnmuteIcon from "../icons/video/VideoUnmuteIcon"
import ImageWithAlt from "../ImageWithAlt"
import NewText from "../NewText/NewText"

const StyledImagesContainer = styled.div<{
  $paddingTop: boolean
  $isForColorPalette?: boolean
  $borderRadius?: string
}>`
  padding-top: ${(p) => (p.$paddingTop ? `38px` : "0px")};
  border-radius: ${(props) => (props.$borderRadius || props.$isForColorPalette ? "16px" : "unset")};
  overflow: ${(props) => (props.$isForColorPalette ? "hidden" : "auto")};
  margin-left: ${(props) => (props.$isForColorPalette ? "16px " : "0px")};
  margin-right: ${(props) => (props.$isForColorPalette ? "16px " : "0px")};
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${(props) => (props.$isForColorPalette ? "1040px" : "unset")};
    max-width: 100%;
    margin: auto;
    padding: 0px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: ${(props) => (props.$isForColorPalette ? "1280px" : "unset")};
  }
`

const StyledActiveImage = styled.div<{ isFullHeight: boolean; imageHeight?: string }>`
  opacity: 1;
  position: relative;
  height: ${(p) => (p.isFullHeight ? "340px" : "392px")};
  display: flex;
  overflow: hidden;
  align-items: end;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: ${(p) => (p.isFullHeight ? "100%" : p.imageHeight || "500px")};
  }
`
const StyledVideoContainer = styled.div<{ $videoHeight?: string }>`
  opacity: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000917;
  height: 340px;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: ${(p) => (p?.$videoHeight && p.$videoHeight !== "100%" ? p.$videoHeight : "500px")};

    overflow: hidden;
  }
  ${(props) => props.theme.breakpoints.down("sm")} {
    > div {
      width: 100%;
      height: 100%;
    }
  }
`
const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${(props) => props.theme.breakpoints.up("md")} {
    position: absolute;
    inset: 0;
  }
`

const StyledContainer = styled(Container)`
  position: absolute;
  inset: 0;
`
const StyledButtonsWrapper = styled.div`
  position: relative;
  z-index: 1;
  direction: rtl;
  display: flex;
  position: absolute;
  inset-inline-start: 0;
  bottom: 20px;
  button {
    padding: 0;
    &:focus-visible {
      border: 2px dashed white;
      outline-offset: 2px;
      outline: 3px dashed #10059f;
    }
  }
  ${(props) => props.theme.breakpoints.down("sm")} {
    bottom: 0px;
    inset-inline-start: 8px;
  }
`

const TextContainer = styled.div`
  position: relative;
  z-index: 2;
  padding: 32px 0;
`
type Props = {
  data: any
  children?: JSX.Element
  isFullHeight?: boolean
  paddingTop?: boolean
  videoHeight?: string
  imageHeight?: string
  borderRadius?: string
  pathname?: string
  titleFontWeight?: number
  titleComponent?: "h1" | "h2" | "h3" | "p"
}

export const TopBanner: React.FC<Props> = ({
  data,
  children,
  isFullHeight = false,
  paddingTop = false,
  videoHeight = "100%",
  imageHeight,
  titleFontWeight,
  titleComponent = "h1",
  borderRadius,
  pathname,
}) => {
  const isForColorPalette = pathname?.endsWith("color-palette")
  const desktopVideo = extractProperty(data, "desktopImage")
  // const mobileVideo = extractProperty(data, "mobileImage")

  const desktopImage = extractImage(data, "desktopImage")
  const mobileImage = extractImage(data, "mobileImage")
  const videoOptional = extractProperty(data, "videoOptional")
  const videoMobile = extractProperty(data, "videoMobile")
  const title = extractProperty(data, "title")
  const hasTitle = typeof title === "string" && title.trim().length > 0
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = true
      setIsMuted(true)
    }
  }

  const handleUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = false
      setIsMuted(false)
    }
  }
  // If neither video nor image is provided, render nothing
  if (!desktopVideo && !desktopImage?.url && !videoOptional) {
    return null
  }
  return (
    <StyledImagesContainer
      $borderRadius={borderRadius}
      $paddingTop={paddingTop}
      $isForColorPalette={isForColorPalette}
    >
      {!videoOptional && !videoMobile ? (
        <StyledActiveImage isFullHeight={isFullHeight} imageHeight={imageHeight}>
          {/* <StyledBackDrop /> */}
          <Container style={{ marginBottom: "unset" }}>
            {hasTitle && (
              <TextContainer>
                <NewText
                  component={titleComponent}
                  desktopFontSize={40}
                  desktopLineHeight={"46px"}
                  desktopFontWeight={titleFontWeight}
                  mobileFontSize={32}
                  mobileLineHeight={"100%"}
                  color="white"
                  sx={{ position: "relative", zIndex: 2 }}
                >
                  {title}
                </NewText>
              </TextContainer>
            )}
          </Container>

          <Desktop>
            <ImageWithAlt image={desktopImage} style={{ objectFit: "cover" }} fill />
          </Desktop>
          <Mobile>
            <ImageWithAlt image={mobileImage} style={{ objectFit: "cover" }} fill />
          </Mobile>
        </StyledActiveImage>
      ) : (
        <StyledVideoContainer $videoHeight={videoHeight}>
          {(videoOptional || videoMobile) && (
            <StyledVideo autoPlay loop muted={isMuted} ref={videoRef} playsInline>
              {videoOptional && (
                <source
                  src={getUmbracoMediaUrl(videoOptional)}
                  type="video/mp4"
                  media="(min-width: 768px)"
                />
              )}
              {videoMobile && (
                <source
                  src={getUmbracoMediaUrl(videoMobile)}
                  type="video/mp4"
                  media="(max-width: 767px)"
                />
              )}
            </StyledVideo>
          )}
          <StyledContainer>
            <StyledButtonsWrapper>
              <IconButton
                onClick={isPlaying ? handlePause : handlePlay}
                aria-label={isPlaying ? "עצירת הוידאו" : "ניגון הוידאו"}
              >
                {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
              </IconButton>
              <IconButton
                onClick={isMuted ? handleUnmute : handleMute}
                aria-label={isMuted ? "השמעת הקול בוידאו" : "השתקת הקול בוידאו"}
              >
                {isMuted ? <VideoUnmuteIcon /> : <VideoMuteIcon />}
              </IconButton>
            </StyledButtonsWrapper>
          </StyledContainer>
        </StyledVideoContainer>
      )}
      <Container>{children}</Container>
    </StyledImagesContainer>
  )
}

export default TopBanner
